<template>
    <div class="navbar__top" data-testid="navbar-top-modes">
        <ul v-if="hasMultipleModes" class="navbar__top-modes">
            <li v-for="availableMode in moduleConfig.modes" :key="availableMode.id" name="navbar-mode-tab">
                <NuxtLink
                    :to="$withLocalePrefix(availableMode.page)"
                    :class="{active: selectedMode === availableMode.id}"
                    :data-testid="`navbar-mode-tab-${availableMode.id}`"
                    :aria-current="selectedMode === availableMode.id ? 'page' : undefined"
                    aria-controls="navbar-content"
                    @click="switchMode(availableMode.id)"
                >
                    <div class="navbar__top-modes-icon">
                        <VJoyIcon :name="availableMode.id === 'client' ? 'company-placeholder' : 'super-malter'" color="neutral-50" />
                        {{ $t(`navbar.top.${availableMode.id}.label` as keyof AllTranslationsRecord) }}
                    </div>
                </NuxtLink>
            </li>
        </ul>
        <div class="navbar__top-language-selector">
            <LocaleSelection />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {NuxtLink} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {useNavbarMode} from '../composables/useNavbarMode';
    import LocaleSelection from '../features/localization-selection/ui/LocaleSelection.vue';
    import type {AllTranslationsRecord} from '#build/i18n';

    const {config: moduleConfig, hasMultipleModes, selectedMode, switchMode} = useNavbarMode();
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .navbar__top {
        display: flex;
        align-items: center;
        padding: 0;
        margin: 0;
        gap: var(--joy-core-spacing-2);
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
        max-height: calc(var(--navbar-height) + var(--joy-core-spacing-8));
        transition:
            opacity var(--navbar-transition-duration) var(--navbar-transition-easing),
            visibility var(--navbar-transition-duration) var(--navbar-transition-easing),
            transform var(--navbar-transition-duration) var(--navbar-transition-easing),
            max-height var(--navbar-transition-duration) var(--navbar-transition-easing);

        &-modes {
            display: flex;
            list-style-type: none;
            gap: var(--joy-core-spacing-2);
            left: 0;
            width: 100%;
            justify-content: center;
            margin-top: var(--joy-core-spacing-3);
            padding-bottom: var(--joy-core-spacing-4);

            &-icon {
                display: flex;
                align-items: center;
                gap: var(--joy-core-spacing-2);
            }
        }

        li {
            display: inline-block;

            a {
                @include text.getFontBySize(primary-450);

                display: block;
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-6);
                color: var(--joy-color-neutral-70);
                text-decoration: none;
                border-radius: var(--joy-core-spacing-6);
                transition: all calc(var(--navbar-transition-duration) * 0.67) var(--navbar-transition-easing);
                background-color: var(--joy-color-neutral-20);

                &:hover,
                &.active {
                    background-color: var(--joy-color-neutral-30);
                }

                &:focus-visible {
                    outline: 1px solid var(--joy-color-neutral-40);
                }
            }
        }

        &-language-selector {
            display: none;
        }
    }

    /* desktop */
    @include mq.screen_md(min) {
        .navbar__top {
            margin-top: var(--joy-core-spacing-3);

            &-modes {
                display: flex;
                position: static;
                width: auto;
                padding: 0;
                margin: 0;
                border-bottom: none;
                background-color: transparent;
                justify-content: flex-start;
            }

            &-language-selector {
                display: block;
                margin-left: auto;
            }
        }
    }
</style>
